import {
  addToaster,
  DropdownType,
  getGenericEntityListingAPI,
  useDispatch,
} from '@app-store'
import {
  Accordion,
  ControlledDropDown,
  CustomButton,
  FormInput,
  SkeletonLoader,
} from '@ui-kit'
import { useEffect, useState } from 'react'
import { noop } from 'lodash'
import { FieldValues, useForm } from 'react-hook-form'
import { v4 as uuid } from 'uuid'
import { NAMESPACES } from '../../utils/constants'
import { editEntityDataEmbeddedAPI } from '../../apis/entityData.api'
import { useAppSelector } from '../../utils/agent.store'
import {
  NovoVoucherInfoProps,
  VoucherFormValues,
} from '../../types/workflow.types'

const NovoVoucherInfo = ({
  task,
  recordId,
  isDisabled,
}: NovoVoucherInfoProps) => {
  const [initialLoadShimmer, setInitialLoadShimmer] = useState<boolean>(true)
  const [initialLoadData, setInitialLoadData] = useState<FieldValues>()
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showReasonField, setShowReasonField] = useState<boolean>(false)
  const [reasonDropDownOptions, setReasonDropDownOptions] = useState<
    DropdownType[]
  >([])
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const dispatch = useDispatch()
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<VoucherFormValues>({
    defaultValues: {
      voucher: '',
      reason_for_applying_voucher: '',
      voucher_id: '',
    },
    mode: 'onBlur',
  })

  const onEditClick = () => {
    setIsEditMode(true)
  }

  const onCancelClick = () => {
    const apiValues: VoucherFormValues = {
      voucher: initialLoadData?.case_pap_details?.voucher ?? '',
      reason_for_applying_voucher:
        initialLoadData?.case_pap_details?.reason_for_applying_voucher ?? '',
      voucher_id: initialLoadData?.case_pap_details?.id ?? '',
    }

    reset(apiValues)
    setIsEditMode(false)
  }

  const onUpdateClick = async () => {
    setIsSaving(true)
    try {
      const values = getValues()

      const record_id = values?.voucher_id ?? ''
      const updateFields = [
        'voucher',
        ...(showReasonField ? ['reason_for_applying_voucher'] : []),
      ] as const

      const payload: VoucherFormValues = {
        voucher: values.voucher,
        ...(showReasonField
          ? { reason_for_applying_voucher: values.reason_for_applying_voucher }
          : {}),
      }

      await editEntityDataEmbeddedAPI('case_pap_details', record_id, payload, [
        ...updateFields,
      ])
      dispatch(
        addToaster({
          message: NAMESPACES.VOUCHER_UPDATED_SUCCESSFULLY,
          type: 'success',
          id: uuid(),
        })
      )
      fetchVoucherData(showReasonField)
      setIsEditMode(false)
    } catch (e) {
      dispatch(
        addToaster({
          message: NAMESPACES.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uuid(),
        })
      )
      setIsEditMode(true)
      console.error(e)
    } finally {
      setIsSaving(false)
    }
  }

  const fetchVoucherData = async (fetchForReason = true) => {
    try {
      const newPayload = {
        entity_name: workflowDefinition?.global_variables?.entity_name,
        select: ['case_pap_details'],
        criteria: [],
        related_entities: {
          case_pap_details: {
            select: [
              'voucher',
              'patient_voucher_state',
              ...(fetchForReason ? ['reason_for_applying_voucher'] : []),
            ],
            criteria: [],
            filter: [],
          },
        },
        filters: [],
      }

      const caseIdObject2 = await getGenericEntityListingAPI(
        newPayload,
        recordId ?? ''
      )
      setInitialLoadData(caseIdObject2)
      setDefaultData(caseIdObject2)
      const showReasonFieldData = task?.identifier?.state_id?.includes(
        caseIdObject2?.case_pap_details?.patient_voucher_state?.id?.toString() ||
          ''
      )
      setShowReasonField(!!showReasonFieldData)
      if (showReasonFieldData) {
        fetchDropDownDataForReason()
      } else setValue('reason_for_applying_voucher', '')
    } catch (error) {
      console.log(error)
    }
  }
  const setDefaultData = (caseIdObject2: FieldValues) => {
    setValue('voucher', caseIdObject2?.case_pap_details?.voucher)
    setValue(
      'reason_for_applying_voucher',
      caseIdObject2?.case_pap_details?.reason_for_applying_voucher
    )
    setValue('voucher_id', caseIdObject2?.case_pap_details?.id)
  }
  const fetchDropDownDataForReason = async () => {
    try {
      const payloadForReason = {
        entity_name: 'd_entity_fields',
        select: ['name', 'control_values'],
        filters: [
          {
            criteria: [
              {
                field_name: 'd_entity_id',
                operation: 'EQUALS',
                values: [task?.identifier?.entity_id?.toString() ?? ''],
              },
              {
                field_name: 'name',
                operation: 'EQUALS',
                values: ['reason_for_applying_voucher'],
              },
            ],
          },
        ],
      }
      const caseIdObject2 = await getGenericEntityListingAPI(payloadForReason)
      const List = caseIdObject2?.[0]?.control_values?.map(
        (option: string) => ({
          label: option,
          value: option,
        })
      )
      setReasonDropDownOptions(List ?? [])
    } catch (error) {
      console.log(error)
    }
  }

  const fetchViewRecordData = async () => {
    try {
      setInitialLoadShimmer(true)
      await fetchVoucherData()
    } catch (err) {
      console.error(err)
    } finally {
      setInitialLoadShimmer(false)
    }
  }

  useEffect(() => {
    fetchViewRecordData()
  }, [])

  return initialLoadShimmer ? (
    <SkeletonLoader />
  ) : (
    <Accordion heading={NAMESPACES.VOUCHER_ACCORDION} isDefaultActive={true}>
      <form
        onSubmit={handleSubmit(onUpdateClick, (errors) => {
          console.log(errors)
        })}
        className="p2"
      >
        <div className={`p-2 flex justify-end gap-3 `}>
          {isEditMode ? (
            <>
              <CustomButton
                isFilled={false}
                disabled={isSaving}
                onClick={onCancelClick}
              >
                {NAMESPACES.CANCEL_BUTTON}
              </CustomButton>

              <CustomButton type="submit" isFilled disabled={isSaving}>
                {NAMESPACES.SAVE}
              </CustomButton>
            </>
          ) : (
            <CustomButton
              className={` ${
                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              isFilled={false}
              disabled={isDisabled}
              onClick={onEditClick}
            >
              {NAMESPACES.EDIT_BUTTON}
            </CustomButton>
          )}
        </div>

        <div className="flex flex-row ">
          <div className={`p-4 flex-1`}>
            <FormInput
              register={register}
              name="voucher"
              label="Voucher #"
              disabled={!isEditMode}
              validation={{
                required: `${NAMESPACES.VOUCHER_REQUIRED}`,

                maxLength: {
                  value: 20,
                  message: `${NAMESPACES.MAX_LENGTH_20}`,
                },
              }}
              errors={errors}
            />
          </div>

          {showReasonField ? (
            <div className="flex-1 p-4">
              <ControlledDropDown
                control={control}
                name={'reason_for_applying_voucher'}
                label="Reason"
                onChange={noop}
                disabled={!isEditMode}
                options={reasonDropDownOptions}
              />
            </div>
          ) : (
            <div className="flex-1 p-4" />
          )}
        </div>
      </form>
    </Accordion>
  )
}

export default NovoVoucherInfo
